import React, { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';
import { Podcast } from '../types';

export const PODCAST_PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;
export type PodcastPlaybackRate = (typeof PODCAST_PLAYBACK_RATES)[number];

interface AudioPlayerContextType {
  currentPodcast: Podcast | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  playbackRate: PodcastPlaybackRate;
  /** پلیر شناور صفحهٔ اصلی؛ وقتی فعال است نوار سراسری نمایش داده نمی‌شود */
  useFloatingPlayerChrome: boolean;
  setUseFloatingPlayerChrome: (active: boolean) => void;
  playPodcast: (podcast: Podcast) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  closePlayer: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: PodcastPlaybackRate) => void;
  togglePlayPause: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: ReactNode;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const [currentPodcast, setCurrentPodcast] = useState<Podcast | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState<PodcastPlaybackRate>(1);
  const [useFloatingPlayerChrome, setUseFloatingPlayerChrome] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Helper: resolve the correct audio URL for a podcast/audio object
  const resolveAudioUrl = (podcast: Podcast | null): string | null => {
    if (!podcast) return null;
    if (podcast.streamUrl) return podcast.streamUrl;
    return null;
  };

  // Create audio element when podcast changes
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
    }

    if (currentPodcast) {
      const audioUrl = resolveAudioUrl(currentPodcast);
      if (audioUrl && audioRef.current.src !== audioUrl) {
        audioRef.current.src = audioUrl;
        audioRef.current.load();
      }
    }
  }, [currentPodcast]);

  // Update current time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
    };
  }, [currentPodcast]);

  // Handle audio end
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [currentPodcast]);

  // Update playing state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [currentPodcast]);

  const playPodcast = (podcast: Podcast) => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
    }

    // If same podcast, just resume
    if (currentPodcast?.id === podcast.id) {
      audioRef.current.play().catch(console.error);
      return;
    }

    // Stop current and load new
    audioRef.current.pause();
    setUseFloatingPlayerChrome(false);
    setCurrentPodcast(podcast);
    
    const audioUrl = resolveAudioUrl(podcast);
    
    if (audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.load();
      audioRef.current.play().catch(console.error);
    }
  };

  const pause = () => {
    audioRef.current?.pause();
  };

  const resume = () => {
    audioRef.current?.play().catch(console.error);
  };

  const closePlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    setCurrentPodcast(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setUseFloatingPlayerChrome(false);
  };

  const stop = () => {
    closePlayer();
  };

  const togglePlayPause = () => {
    if (!audioRef.current || !currentPodcast) return;
    
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  };

  const seek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const setVolume = (newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  };

  const setPlaybackRate = (rate: PodcastPlaybackRate) => {
    setPlaybackRateState(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentPodcast,
        isPlaying,
        currentTime,
        duration,
        volume,
        playbackRate,
        useFloatingPlayerChrome,
        setUseFloatingPlayerChrome,
        playPodcast,
        pause,
        resume,
        stop,
        closePlayer,
        seek,
        setVolume,
        setPlaybackRate,
        togglePlayPause,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};
